import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, AlertCircle, Calendar, Trash2, Wand, Eye, ChevronDown, Plus, Tag } from "lucide-react";
import { useProjects } from "../contexts/ProjectContext";
import { useNavigate } from "react-router-dom";
import api from "../utils/apiClient";
import KeywordGenerationModal from "./KeywordGenerationModal";

// Utility function to format date
const formatDate = (date) => {
  return new Date(date).toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

// Utility function to get day name
const getDayName = (date) => {
  return new Date(date).toLocaleDateString("en-US", { weekday: "short" });
};

// Utility function to format month name
const getMonthName = (date) => {
  return new Date(date).toLocaleDateString("en-US", { month: "long", year: "numeric" });
};

// Add Keyword Modal Component
const AddKeywordModal = ({ isOpen, onClose, onSave, cards }) => {
  const { currentProject } = useProjects();
  const [keywords, setKeywords] = useState([]);
  const [keywordsInput, setKeywordsInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setKeywords([]);
      setKeywordsInput("");
      setError("");
    }
  }, [isOpen]);

  // Handle input change
  const handleInputChange = (e) => {
    setKeywordsInput(e.target.value);
  };

  // Handle pressing Enter to add keyword
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && keywordsInput.trim()) {
      e.preventDefault();
      addKeyword();
    }
  };

  // Add keyword from input
  const addKeyword = () => {
    const newKeyword = keywordsInput.trim();

    if (newKeyword && !keywords.includes(newKeyword)) {
      setKeywords([...keywords, newKeyword]);
      setKeywordsInput("");
    }
  };

  // Remove keyword
  const removeKeyword = (keywordToRemove) => {
    setKeywords(keywords.filter(k => k !== keywordToRemove));
  };

  // Save keywords
  const handleSave = async () => {
    if (keywords.length === 0) {
      setError("Please add at least one keyword");
      return;
    }

    setSaving(true);
    setError("");

    try {
      // First, get the current project details to determine existing keywords
      const { data: project } = await api.projects.get(currentProject.id);

      // Extract existing keywords
      const existingKeywords = project.project_keywords?.map(k => k.keyword) || [];

      // Combine existing and new keywords
      const allKeywords = [...new Set([...existingKeywords, ...keywords])];

      // Update keywords in the project
      await api.projects.upsertRegionAndKeywords(currentProject.id, {
        keyword_region: project.keyword_region || "Default Region",
        keywords: allKeywords
      });

      // Schedule the keywords

      // Get the latest scheduled keyword to determine next scheduling date
      const sortedCards = [...cards].sort((a, b) => {
        const dateA = a.date instanceof Date ? a.date : new Date(a.date);
        const dateB = b.date instanceof Date ? b.date : new Date(b.date);
        return dateB - dateA; // Sort descending
      });

      let startDate;

      if (sortedCards.length > 0) {
        // If we have existing keywords, start from the day after the latest
        startDate = new Date(sortedCards[0].date);
        startDate.setDate(startDate.getDate() + 1);
      } else {
        // If no existing keywords, start from tomorrow
        startDate = new Date();
        startDate.setDate(startDate.getDate() + 1);
      }

      // Set to noon to avoid timezone issues
      startDate.setHours(12, 0, 0, 0);

      // Create a single request with all the keywords
      try {
        // This is a custom implementation that adds the ability to schedule specific keywords on specific dates
        await scheduleCustomKeywords(currentProject.id, keywords, startDate);

        // After scheduling, refresh the keywords list
        onSave(keywords);
        onClose();
      } catch (scheduleError) {
        console.error("Error scheduling keywords:", scheduleError);
        setError("Failed to schedule keywords. Try again later.");
      }
    } catch (err) {
      console.error("Error saving keywords:", err);
      setError(err.response?.data?.error || "Failed to save keywords");
    } finally {
      setSaving(false);
    }
  };

  // Custom function to schedule keywords with dates
  const scheduleCustomKeywords = async (projectId, keywordsToSchedule, startDate) => {
    // Get the latest scheduled date
    const sortedCards = [...cards].sort((a, b) => {
      const dateA = a.date instanceof Date ? a.date : new Date(a.date);
      const dateB = b.date instanceof Date ? b.date : new Date(b.date);
      return dateB - dateA; // Sort descending
    });

    let schedulingDate;

    if (sortedCards.length > 0) {
      // Start from the day after the latest scheduled date
      schedulingDate = new Date(sortedCards[0].date);
      schedulingDate.setDate(schedulingDate.getDate() + 1);
    } else {
      // If no existing keywords, start from tomorrow
      schedulingDate = new Date();
      schedulingDate.setDate(schedulingDate.getDate() + 1);
    }

    // Set to noon to avoid timezone issues
    schedulingDate.setHours(12, 0, 0, 0);

    // Track scheduled keywords for the response
    const scheduledKeywords = [];

    // Schedule each keyword individually
    for (const keyword of keywordsToSchedule) {
      try {
        // Create a new keyword entry directly
        const response = await api.generatedKeywords.directAdd(projectId, {
          keyword: keyword,
          scheduled_for: schedulingDate.toISOString(),
          user_generated: true
        });

        if (response.data) {
          scheduledKeywords.push(keyword);
        }

        // Move to the next day for the next keyword
        schedulingDate.setDate(schedulingDate.getDate() + 1);
      } catch (error) {
        // Continue with next keyword even if this one fails
      }
    }

    return scheduledKeywords;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl w-full max-w-md shadow-xl transform transition-all duration-300 ease-out max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="px-6 pt-6 pb-4 border-b border-gray-100">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-primary-100 rounded-lg">
              <Tag className="w-5 h-5 text-primary-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-900">Add Keywords</h2>
          </div>
          <p className="text-gray-600 text-sm">
            Add keywords to your project. These will be available for future content generation.
          </p>
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all duration-200"
          >
            <X size={20} />
          </button>
        </div>

        {/* Error message */}
        {error && (
          <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-100 rounded-lg flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Content */}
        <div className="p-6">
          <div className="space-y-6">
            {/* Add Keywords Input */}
            <div className="space-y-3">
              <label className="text-sm font-medium text-gray-700 block">
                Enter Keywords
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={keywordsInput}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  placeholder="Type and press Enter to add"
                  className="block w-full pl-4 pr-10 py-3 text-base border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent rounded-lg bg-gray-50 hover:bg-white transition-all duration-200"
                />
                <button
                  onClick={addKeyword}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1.5 text-gray-400 hover:text-primary-600 hover:bg-gray-100 rounded-lg transition-all duration-200"
                >
                  <Plus size={18} />
                </button>
              </div>
              <p className="text-xs text-gray-500">
                Press Enter after each keyword to add it
              </p>
            </div>

            {/* Keywords Display */}
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-2">
                Keywords to Add ({keywords.length})
              </label>
              <div className="min-h-[100px] max-h-[200px] overflow-y-auto border border-gray-200 rounded-lg p-3 bg-gray-50">
                {keywords.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {keywords.map((keyword, index) => (
                      <div
                        key={index}
                        className="bg-white px-3 py-1.5 rounded-full border border-gray-200 flex items-center gap-1.5 text-sm text-gray-700 shadow-sm"
                      >
                        <span>{keyword}</span>
                        <button
                          onClick={() => removeKeyword(keyword)}
                          className="text-gray-400 hover:text-red-500"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <p className="text-gray-400 text-sm">No keywords added yet</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={keywords.length === 0 || saving}
            className={`px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 flex items-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed`}
          >
            {saving ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Saving...
              </>
            ) : (
              <>Save Keywords</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const ContentPlanner = () => {
  const [cards, setCards] = useState([]);
  const [calendarDays, setCalendarDays] = useState([]);
  const [deleteConfirmation, setDeleteConfirmation] = useState(null);
  const [draggedCard, setDraggedCard] = useState(null);
  const [calendarMonths, setCalendarMonths] = useState([]);
  const [keywordsLoading, setKeywordsLoading] = useState(false);
  const [generatingKeywords, setGeneratingKeywords] = useState(false);
  const [generatingContentIds, setGeneratingContentIds] = useState([]);
  const [expandedMonth, setExpandedMonth] = useState(null);
  const [isGenerateModalOpen, setIsGenerateModalOpen] = useState(false);
  const [isAddKeywordModalOpen, setIsAddKeywordModalOpen] = useState(false);
  const { currentProject } = useProjects();
  const navigate = useNavigate();

  // Initialize calendar dates
  useEffect(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Find the first day of the current week (Monday)
    const startOfCurrentWeek = new Date(today);
    const dayOfWeek = today.getDay(); // 0 is Sunday, 1 is Monday, etc.
    const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Convert to 0 = Monday, 6 = Sunday
    startOfCurrentWeek.setDate(today.getDate() - daysFromMonday); // Go back to Monday

    // Create a date for 3 months into the future
    const endDate = new Date(today);
    endDate.setMonth(today.getMonth() + 3);

    // Generate days from the start of current week to 3 months from now
    const days = [];
    const currentDate = new Date(startOfCurrentWeek);

    while (currentDate <= endDate) {
      days.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }

    setCalendarDays(days);

    // Group days by month
    const months = [];
    let currentMonth = null;
    let currentMonthDays = [];

    days.forEach((day) => {
      const monthYear = day.toLocaleDateString("en-US", { month: "long", year: "numeric" });

      if (currentMonth !== monthYear) {
        if (currentMonth) {
          months.push({
            name: currentMonth,
            days: currentMonthDays,
          });
        }

        currentMonth = monthYear;
        currentMonthDays = [day];
      } else {
        currentMonthDays.push(day);
      }
    });

    // Add the last month
    if (currentMonthDays.length > 0) {
      months.push({
        name: currentMonth,
        days: currentMonthDays,
      });
    }

    setCalendarMonths(months);

    // Set current month as expanded by default
    const currentMonthName = today.toLocaleDateString("en-US", { month: "long", year: "numeric" });
    setExpandedMonth(currentMonthName);

    // Also set next month as expanded to see upcoming scheduled keywords
    const nextMonth = new Date(today);
    nextMonth.setMonth(today.getMonth() + 1);
    const nextMonthName = nextMonth.toLocaleDateString("en-US", { month: "long", year: "numeric" });

    // If we have both current and next month, set both as expanded
    if (currentMonthName !== nextMonthName) {
      setExpandedMonth((prev) => [currentMonthName, nextMonthName]);
    }
  }, []);

  // Fetch and process keyword data when project changes
  useEffect(() => {
    if (currentProject) {
      getKeywords();
    }
  }, [currentProject]);

  // Handle drag start
  const handleDragStart = (card) => {
    setDraggedCard(card);
  };

  const generateKeywords = () => {
    setIsGenerateModalOpen(true);
  };

  const handleAddKeywords = () => {
    setIsAddKeywordModalOpen(true);
  };

  const handleKeywordsSave = async (newKeywords) => {
    // Refresh keywords with a force refresh
    await getKeywords(true);
  };

  const handleGenerateSubmit = async (formData) => {
    try {
      setGeneratingKeywords(true);

      // Generate keyword suggestions if needed
      if (formData.generateNewSuggestions) {
        try {
          await api.keywords.generateSuggestions(currentProject.id);
        } catch (error) {
          console.error("Error generating suggestions:", error);
          setGeneratingKeywords(false);
          setIsGenerateModalOpen(false);
          return;
        }
      }

      // Schedule keywords from the suggestions
      try {
        const response = await api.generatedKeywords.add(currentProject.id, {
          numKeywords: formData.numKeywords
        });

        // Refresh keywords after scheduling
        getKeywords(true);

      } catch (scheduleError) {
        console.error("Schedule error:", scheduleError);

        // Handle specific error for no new keywords or no suggestions available
        const errorMessage = scheduleError.response?.data?.error || '';
        if (scheduleError.response?.status === 400 &&
          (errorMessage.includes('No new keywords to schedule') ||
            errorMessage.includes('No keyword suggestions available'))) {

          console.log("Generating new keyword suggestions automatically...");

          // Automatically generate new suggestions
          try {
            // Generate new keyword suggestions
            await api.keywords.generateSuggestions(currentProject.id);
            console.log("New keyword suggestions generated");

            // Try scheduling again
            await api.generatedKeywords.add(currentProject.id, {
              numKeywords: formData.numKeywords
            });

            // Refresh keywords
            getKeywords(true);
          } catch (error) {
            console.error("Error in automatic retry:", error);
          }
        }
      }
    } catch (error) {
      console.error("General error:", error);
    } finally {
      setGeneratingKeywords(false);
      setIsGenerateModalOpen(false);
    }
  };

  // Fetch keywords efficiently
  const getKeywords = async (force = false) => {
    if (!currentProject || (keywordsLoading && !force)) {
      return;
    }

    try {
      setKeywordsLoading(true);

      const res = await api.generatedKeywords.list(currentProject.id, {
        includeDeleted: false,
      });

      // Transform API response to the card format
      const transformedCards = res.data.map((keyword) => {
        // Parse the date and normalize to noon to avoid timezone issues
        let scheduledDate = new Date(keyword.scheduled_for);

        // Validate date or use fallback
        if (isNaN(scheduledDate.getTime())) {
          scheduledDate = new Date();
        }

        // Set to noon to avoid timezone issues
        scheduledDate.setHours(12, 0, 0, 0);

        return {
          id: keyword.id,
          title: keyword.keyword,
          date: scheduledDate,
          volume: keyword.search_volume,
          difficulty: keyword.difficulty,
          status: keyword.status,
          user_generated: keyword.user_generated || false,
        };
      });

      setCards(transformedCards);
    } finally {
      setKeywordsLoading(false);
    }
  };

  // Generate content for a specific card
  const generateContent = async (card) => {
    const cardId = card.id;

    // Add card ID to generating content IDs
    setGeneratingContentIds(prev => [...prev, cardId]);

    // Update UI to show generating status
    setCards(prevCards => prevCards.map(c =>
      c.id === cardId ? { ...c, status: "generating" } : c
    ));

    try {
      // Call API to generate content
      await api.blogGeneration.generate({
        keywordId: cardId,
        projectId: currentProject.id,
      });

      // Update the card status on success
      setCards(prevCards => prevCards.map(c =>
        c.id === cardId ? { ...c, status: "generated" } : c
      ));
    } catch (error) {
      // Revert UI change on error
      setCards(prevCards => prevCards.map(c =>
        c.id === cardId ? { ...c, status: "pending" } : c
      ));
    } finally {
      // Remove card ID from generating content IDs
      setGeneratingContentIds(prev => prev.filter(id => id !== cardId));
    }
  };

  // View blog content associated with a keyword
  const viewBlogContent = (card) => {
    navigate(`/dashboard/content/${card.id}`);
  };

  // Handle drop on date
  const handleDrop = async (targetDate) => {
    if (!draggedCard) return;

    try {
      const newCards = [...cards];
      const draggedCardIndex = newCards.findIndex((c) => c.id === draggedCard.id);

      // Find if there's a card already on target date
      const targetCardIndex = newCards.findIndex(
        (c) => c.id !== draggedCard.id && c.date.toDateString() === targetDate.toDateString()
      );

      if (targetCardIndex !== -1) {
        // Swap dates between cards
        const tempDate = new Date(newCards[draggedCardIndex].date);
        const targetCardDate = new Date(newCards[targetCardIndex].date);

        // Update the UI immediately
        newCards[draggedCardIndex].date = targetCardDate;
        newCards[targetCardIndex].date = tempDate;
        setCards(newCards);

        // Update both keywords in database
        try {
          await Promise.all([
            api.generatedKeywords.update(newCards[draggedCardIndex].id, {
              scheduled_for: newCards[draggedCardIndex].date.toISOString()
            }),
            api.generatedKeywords.update(newCards[targetCardIndex].id, {
              scheduled_for: newCards[targetCardIndex].date.toISOString()
            })
          ]);
        } catch (error) {
          // Revert UI changes on error
          newCards[draggedCardIndex].date = tempDate;
          newCards[targetCardIndex].date = targetCardDate;
          setCards(newCards);
        }
      } else {
        // Just update date for dragged card
        const oldDate = new Date(newCards[draggedCardIndex].date);
        const newDate = new Date(targetDate);

        // Update UI immediately
        newCards[draggedCardIndex].date = newDate;
        setCards(newCards);

        // Update the backend
        try {
          await api.generatedKeywords.update(draggedCard.id, {
            scheduled_for: newDate.toISOString()
          });
        } catch (error) {
          // Revert UI change on error
          newCards[draggedCardIndex].date = oldDate;
          setCards(newCards);
        }
      }
    } finally {
      setDraggedCard(null);
    }
  };

  // Handle card deletion
  const deleteCard = async (cardId) => {
    // Update UI immediately for responsiveness
    setCards((prevCards) => prevCards.filter((card) => card.id !== cardId));

    try {
      await api.generatedKeywords.softDelete(cardId);
    } catch (error) {
      // Refresh on error to restore accurate state
      getKeywords();
    }

    setDeleteConfirmation(null);
  };

  // Group calendar dates by week within each month
  const getCalendarWeeksForDays = (days) => {
    const weeks = [];
    let currentWeek = [];

    days.forEach((date, index) => {
      const dayOfWeek = date.getDay(); // 0 is Sunday, 1 is Monday, etc.
      const weekdayIndex = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Convert to 0 = Monday, 6 = Sunday

      // If this is a new week and we need to pad with empty cells
      if (currentWeek.length === 0 && weekdayIndex > 0 && index === 0) {
        for (let i = 0; i < weekdayIndex; i++) {
          currentWeek.push(null); // Add empty spot
        }
      }

      currentWeek.push(date);

      // If we've reached the end of a week or the last day
      if (weekdayIndex === 6 || index === days.length - 1) {
        // If we need to pad the end of the week
        if (weekdayIndex !== 6) {
          for (let i = weekdayIndex + 1; i <= 6; i++) {
            currentWeek.push(null); // Add empty spot
          }
        }

        weeks.push(currentWeek);
        currentWeek = [];
      }
    });

    return weeks;
  };

  // Get card for a specific date
  const getCardForDate = (date) => {
    if (!date) return null;

    // Convert the calendar date to a date string (ignoring time)
    const dateObj = new Date(date);
    const targetYear = dateObj.getFullYear();
    const targetMonth = dateObj.getMonth();
    const targetDay = dateObj.getDate();

    // Look for a card where the date (ignoring time) matches the calendar date
    return cards.find(card => {
      if (!card.date) return false;

      // Convert card date to a Date object if it's a string
      const cardDate = card.date instanceof Date ? card.date : new Date(card.date);

      // Compare year, month, and day instead of date strings
      return cardDate.getFullYear() === targetYear &&
        cardDate.getMonth() === targetMonth &&
        cardDate.getDate() === targetDay;
    });
  };

  // Get card style based on status
  const getCardStyle = (card) => {
    if (card.status === "generating") {
      return "border-l-4 border-amber-500 bg-amber-50 shadow-sm";
    } else if (card.status === "generated") {
      return "border-l-4 border-emerald-500 bg-emerald-50 shadow-sm";
    }
    return "border-l-4 border-gray-300 bg-white shadow-sm";
  };

  // Check if a date is in the past
  const isPastDate = (date) => {
    if (!date) return false;
    return date < new Date(new Date().setHours(0, 0, 0, 0));
  };

  // Check if a date is today
  const isToday = (date) => {
    if (!date) return false;
    return new Date().toDateString() === date.toDateString();
  };

  // Toggle month expansion
  const toggleMonthExpansion = (monthName) => {
    setExpandedMonth((prev) => {
      // If prev is not an array, convert it to an array
      const prevArray = Array.isArray(prev) ? prev : prev ? [prev] : [];

      // Check if the month is already expanded
      if (prevArray.includes(monthName)) {
        // Remove it from the array
        return prevArray.filter(month => month !== monthName);
      } else {
        // Add it to the array
        return [...prevArray, monthName];
      }
    });
  };

  // Count cards in a month
  const countCardsInMonth = (days) => {
    return days.reduce((count, day) => {
      const card = getCardForDate(day);
      return card ? count + 1 : count;
    }, 0);
  };

  return (
    <div className="flex flex-col h-full bg-gray-50">
      {/* Add the modal component */}
      <KeywordGenerationModal
        isOpen={isGenerateModalOpen}
        onClose={() => setIsGenerateModalOpen(false)}
        onGenerate={handleGenerateSubmit}
        isLoading={generatingKeywords}
      />

      {/* Add Keywords Modal */}
      <AddKeywordModal
        isOpen={isAddKeywordModalOpen}
        onClose={() => setIsAddKeywordModalOpen(false)}
        onSave={handleKeywordsSave}
        cards={cards}
      />

      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center p-4 bg-white shadow-sm sticky top-0 z-10 gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-800 leading-tight">Content Planner</h1>
          <div className="text-sm text-gray-500 mt-1">Schedule and manage your keywords for upcoming dates</div>
        </div>

        <div className="flex gap-2 w-full md:w-auto justify-end">
          <button
            className={`px-3 py-1.5 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors shadow-sm font-medium text-sm flex items-center justify-center gap-1.5 ${generatingKeywords ? "opacity-70 cursor-not-allowed" : ""
              }`}
            onClick={generateKeywords}
            disabled={generatingKeywords}
          >
            {generatingKeywords ? (
              <>
                <svg
                  className="animate-spin h-4 w-4 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                <span className="ml-1">Generating...</span>
              </>
            ) : (
              <>
                <Wand size={14} />
                <span>Generate Keywords</span>
              </>
            )}
          </button>
          <button
            onClick={handleAddKeywords}
            className="px-3 py-1.5 bg-white text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors shadow-sm flex items-center justify-center gap-1.5 font-medium text-sm"
          >
            <span>Add Keywords</span>
            <Plus size={14} />
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="w-full px-2 py-6 md:py-8 lg:pl-6 lg:pr-6">
        {keywordsLoading && cards.length === 0 ? (
          <div className="flex justify-center items-center h-64 bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="flex flex-col items-center">
              <svg
                className="animate-spin h-10 w-10 text-primary-500 mb-4"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              <div className="text-xl text-gray-600 font-medium">Loading keywords...</div>
            </div>
          </div>
        ) : cards.length === 0 ? (
          <div className="flex justify-center items-center h-64 bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="flex flex-col items-center max-w-md text-center px-4">
              <Calendar size={56} className="text-gray-300 mb-5" />
              <div className="text-2xl text-gray-700 font-medium mb-3">No keywords found</div>
              <p className="text-gray-500 mb-6 text-lg">
                Generate some keywords to start planning your content calendar.
              </p>
              <button
                className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors shadow-sm font-medium text-sm flex items-center gap-2"
                onClick={generateKeywords}
              >
                <Wand size={16} />
                Generate Keywords
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {calendarMonths.map((month, monthIndex) => (
              <div
                key={month.name}
                className="mb-6 border border-gray-200 rounded-xl overflow-hidden shadow-sm bg-white"
              >
                {/* Month Header - Clickable */}
                <button
                  onClick={() => toggleMonthExpansion(month.name)}
                  className="w-full text-left bg-gray-50 hover:bg-gray-100 transition-colors px-4 py-3 flex justify-between items-center border-b border-gray-200"
                >
                  <div className="flex items-center">
                    <h2 className="text-lg font-bold text-gray-800">{month.name}</h2>
                    <span className="ml-3 text-xs bg-primary-100 text-primary-700 px-2 py-0.5 rounded-full font-medium">
                      {countCardsInMonth(month.days)} keywords
                    </span>
                  </div>
                  <ChevronDown
                    size={18}
                    className={`text-gray-500 transition-transform duration-300 ${(Array.isArray(expandedMonth) ? expandedMonth.includes(month.name) : expandedMonth === month.name)
                        ? "rotate-180"
                        : "rotate-0"
                      }`}
                  />
                </button>

                {/* Calendar Weeks for this month - Collapsible */}
                {(Array.isArray(expandedMonth) ? expandedMonth.includes(month.name) : expandedMonth === month.name) && (
                  <div className="bg-white p-4">
                    {getCalendarWeeksForDays(month.days).map((week, weekIndex) => (
                      <div
                        key={`${month.name}-week-${weekIndex}`}
                        className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-7 gap-2 mb-3"
                      >
                        {week.map((date, dateIndex) => {
                          if (!date) {
                            // Return an empty cell if there's no date - only show on larger screens
                            return (
                              <div
                                key={`empty-${monthIndex}-${weekIndex}-${dateIndex}`}
                                className="hidden xl:block min-h-[200px]"
                              ></div>
                            );
                          }

                          const card = getCardForDate(date);
                          const todayCheck = isToday(date);
                          const isWeekend = dateIndex >= 5;
                          const isPast = isPastDate(date);

                          return (
                            <motion.div
                              key={date.toDateString()}
                              className={`
                                relative bg-white border rounded-lg overflow-hidden shadow-sm
                                ${todayCheck ? "ring-1 ring-primary-400" : ""}
                                ${isPast ? "opacity-75" : ""}
                                hover:shadow-md transition-all duration-200 min-h-[200px]
                              `}
                              onDragOver={(e) => e.preventDefault()}
                              onDrop={() => handleDrop(date)}
                            >
                              {/* Date Header */}
                              <div
                                className={`px-2 py-1.5 font-medium border-b ${todayCheck
                                  ? "bg-primary-50 border-primary-100"
                                  : isPast
                                    ? "bg-gray-50 border-gray-100"
                                    : "bg-white border-gray-100"
                                  } flex justify-between`}
                              >
                                <span
                                  className={`text-sm ${todayCheck ? "text-primary-700 font-bold" : isPast ? "text-gray-400" : "text-gray-700"
                                    }`}
                                >
                                  {date.getDate()}
                                </span>
                                <span className={`text-xs ${isPast ? "text-gray-400" : "text-gray-500"}`}>
                                  {getDayName(date)}
                                </span>
                              </div>

                              {/* Card Content */}
                              <div className="p-2 h-[165px] flex flex-col overflow-hidden">
                                <AnimatePresence>
                                  {card ? (
                                    <motion.div
                                      layout
                                      initial={{ opacity: 0, y: 5 }}
                                      animate={{ opacity: 1, y: 0 }}
                                      exit={{ opacity: 0, scale: 0.95 }}
                                      transition={{ type: "spring", stiffness: 300, damping: 30 }}
                                      className={`
                                        rounded cursor-grab relative group h-full flex flex-col border border-gray-200
                                        ${getCardStyle(card)}
                                      `}
                                      draggable
                                      onDragStart={() => handleDragStart(card)}
                                    >
                                      <div className="p-2.5 flex-grow overflow-hidden">
                                        {/* Delete Button */}
                                        <button
                                          className="absolute top-1 right-1 bg-white rounded-full p-0.5 shadow-sm opacity-0 group-hover:opacity-100 transition-opacity z-10 hover:bg-red-50"
                                          onClick={() => setDeleteConfirmation(card.id)}
                                        >
                                          <X size={13} className="text-gray-500 hover:text-red-500" />
                                        </button>

                                        {/* Card Title */}
                                        <h3 className="text-sm font-medium text-gray-800 mb-2 break-words pr-4 leading-tight line-clamp-3">
                                          {card.title}
                                        </h3>

                                        {/* Card Metrics */}
                                        <div className="flex justify-between items-center gap-1 mt-auto mb-2">
                                          {!card.user_generated && (
                                            <>
                                              <span className="px-1.5 py-0.5 bg-white rounded text-xs text-gray-600 shadow-sm whitespace-nowrap">
                                                Vol: {card.volume}
                                              </span>
                                              <span className="px-1.5 py-0.5 bg-white rounded text-xs text-gray-600 shadow-sm whitespace-nowrap">
                                                Diff: {card.difficulty}
                                              </span>
                                            </>
                                          )}
                                        </div>
                                      </div>

                                      {/* Card Status and Action Button */}
                                      <div className="mt-auto p-2 pt-1.5 border-t border-gray-200 w-full">
                                        {card.status === "generated" ? (
                                          <div className="flex items-center justify-between w-full gap-2">
                                            <div className="inline-flex items-center h-6 bg-emerald-100 border border-emerald-300 rounded px-2">
                                              <span className="text-xs text-emerald-700 font-medium whitespace-nowrap">Published</span>
                                            </div>
                                            <button
                                              onClick={() => viewBlogContent(card)}
                                              className="inline-flex items-center h-6 bg-primary-500 text-white text-xs px-2.5 rounded"
                                            >
                                              View
                                            </button>
                                          </div>
                                        ) : card.status === "generating" ? (
                                          <div className="flex items-center justify-between w-full">
                                            <div className="flex items-center">
                                              <svg
                                                className="animate-spin mr-1 h-3 w-3 text-amber-500"
                                                xmlns="http://www.w3.org/2000/svg"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                              >
                                                <circle
                                                  className="opacity-25"
                                                  cx="12"
                                                  cy="12"
                                                  r="10"
                                                  stroke="currentColor"
                                                  strokeWidth="4"
                                                ></circle>
                                                <path
                                                  className="opacity-75"
                                                  fill="currentColor"
                                                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                                ></path>
                                              </svg>
                                              <span className="text-xs text-amber-600">Generating...</span>
                                            </div>
                                            <div className="inline-flex items-center h-6 bg-amber-100 border border-amber-300 rounded px-2">
                                              <span className="text-xs text-amber-700 font-medium whitespace-nowrap">In Progress</span>
                                            </div>
                                          </div>
                                        ) : (
                                          isToday(date) ? (
                                            <button
                                              onClick={() => generateContent(card)}
                                              disabled={generatingContentIds.includes(card.id)}
                                              className={`${generatingContentIds.includes(card.id)
                                                  ? "bg-gray-400 cursor-not-allowed"
                                                  : "bg-primary-500 hover:bg-primary-600"
                                                } text-white text-xs px-2.5 h-6 rounded w-full transition-colors flex items-center justify-center`}
                                            >
                                              {generatingContentIds.includes(card.id) ? (
                                                <>
                                                  <svg
                                                    className="animate-spin mr-1 h-3 w-3 text-white"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    fill="none"
                                                    viewBox="0 0 24 24"
                                                  >
                                                    <circle
                                                      className="opacity-25"
                                                      cx="12"
                                                      cy="12"
                                                      r="10"
                                                      stroke="currentColor"
                                                      strokeWidth="4"
                                                    ></circle>
                                                    <path
                                                      className="opacity-75"
                                                      fill="currentColor"
                                                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                                    ></path>
                                                  </svg>
                                                  Generating...
                                                </>
                                              ) : (
                                                "Create & Publish"
                                              )}
                                            </button>
                                          ) : (
                                            <div className="inline-flex items-center h-6 bg-gray-100 border border-gray-200 rounded px-2 w-full justify-center">
                                              <span className="text-xs text-gray-500 font-medium whitespace-nowrap">Scheduled</span>
                                            </div>
                                          )
                                        )}
                                      </div>
                                    </motion.div>
                                  ) : (
                                    <div className="flex items-center justify-center h-full">
                                      <p className="text-gray-400 text-sm italic">No content scheduled</p>
                                    </div>
                                  )}
                                </AnimatePresence>
                              </div>
                            </motion.div>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {deleteConfirmation && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-lg p-4 max-w-sm w-full mx-auto shadow-xl"
            >
              <div className="flex items-center text-red-500 mb-3">
                <AlertCircle className="mr-2" size={20} />
                <h3 className="text-base font-bold">Delete Confirmation</h3>
              </div>
              <p className="text-gray-600 mb-4 text-sm">
                Are you sure you want to delete this keyword? This action cannot be undone.
              </p>
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setDeleteConfirmation(null)}
                  className="px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-gray-700 font-medium text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={() => deleteCard(deleteConfirmation)}
                  className="px-3 py-1.5 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors flex items-center justify-center font-medium text-sm"
                >
                  <Trash2 size={14} className="mr-1.5" />
                  Delete
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ContentPlanner;